import React, { useMemo, useState } from "react";
import { MenuItem, TextField } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  useArchiveCmpEventTypeMutation,
  useCreateCmpEventTypeMutation,
  useGetCmpEventTypesQuery,
  useUpdateCmpEventTypeMutation,
} from "../../../../../graphql/types";
import ColorPicker from "../../../../common/FormComponents/ColorPicker";
import { cpaGold } from "../../EventSales/eventSalesShared";
import { FieldLabel, WizardActionButton, wizardTextFieldSx } from "./wizardShared";
import {
  DeleteConfirmModal,
  LibraryEditDialog,
  SuccessPopup,
} from "./EventLibrarySelect";

type EventTypeSelectProps = {
  chamberId: string;
  value: string;
  currentLabel?: string;
  currentColor?: string;
  onChange: (selection: { id: string; label: string; color: string }) => void;
  error?: string;
};

// The Add Event Type mock opens with the purple swatch the color picker shows
// as HEX #9751F2 (event type popups sheet).
const DEFAULT_COLOR = "#9751F2";

const TypeDot = ({ color }: { color: string }): React.ReactElement => (
  <span
    className="mr-2 inline-block h-3 w-3 rounded-full align-middle"
    style={{ backgroundColor: color }}
  />
);

const EventTypeSelect = ({
  chamberId,
  value,
  currentLabel,
  currentColor,
  onChange,
  error,
}: EventTypeSelectProps): React.ReactElement => {
  const { data, refetch } = useGetCmpEventTypesQuery({
    variables: { input: { chamberId, includeArchived: false } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const eventTypes = useMemo(
    () => data?.getCmpEventTypes.eventTypes || [],
    [data]
  );
  const selected = eventTypes.find((eventType) => eventType.id === value);
  const selectedLabel = selected?.label || currentLabel || "";
  const selectedColor = selected?.color || currentColor || cpaGold;

  const [createType, { loading: creating }] = useCreateCmpEventTypeMutation();
  const [updateType, { loading: updating }] = useUpdateCmpEventTypeMutation();
  const [archiveType, { loading: archiving }] = useArchiveCmpEventTypeMutation();

  const [dialog, setDialog] = useState<"add" | "edit" | null>(null);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftColor, setDraftColor] = useState(DEFAULT_COLOR);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const openAdd = () => {
    setDraftLabel("");
    setDraftColor(DEFAULT_COLOR);
    setDialog("add");
  };
  const openEdit = () => {
    if (!selected) {
      return;
    }
    setDraftLabel(selected.label);
    setDraftColor(selected.color);
    setDialog("edit");
  };

  const handleSave = async () => {
    if (!draftLabel.trim()) {
      return;
    }
    if (dialog === "add") {
      const created = await createType({
        variables: {
          input: { chamberId, label: draftLabel.trim(), color: draftColor },
        },
      });
      await refetch();
      const item = created.data?.createCmpEventType?.eventType;
      if (item) {
        onChange({ id: item.id, label: item.label, color: item.color });
      }
    } else if (dialog === "edit" && selected) {
      const updated = await updateType({
        variables: {
          input: {
            chamberId,
            eventTypeId: selected.id,
            label: draftLabel.trim(),
            color: draftColor,
          },
        },
      });
      await refetch();
      const item = updated.data?.updateCmpEventType?.eventType;
      if (item) {
        onChange({ id: item.id, label: item.label, color: item.color });
      }
    }
    setDialog(null);
  };

  const handleDelete = async () => {
    if (!selected) {
      return;
    }
    await archiveType({
      variables: { input: { chamberId, eventTypeId: selected.id } },
    });
    await refetch();
    onChange({ id: "", label: "", color: cpaGold });
    setConfirmDelete(false);
    setResult("Event Type Deleted Successfully!");
  };

  return (
    <div>
      <FieldLabel required hint="Choose the event type and assign a color to display on your calendar.">
        Event Type
      </FieldLabel>
      <TextField
        select
        fullWidth
        size="small"
        value={value}
        onChange={({ target }) => {
          const next = eventTypes.find((eventType) => eventType.id === target.value);
          onChange({
            id: target.value,
            label: next?.label || "",
            color: next?.color || cpaGold,
          });
        }}
        error={Boolean(error)}
        helperText={error}
        sx={wizardTextFieldSx}
        SelectProps={{
          displayEmpty: true,
          renderValue: () =>
            value && selectedLabel ? (
              <span>
                <TypeDot color={selectedColor} />
                {selectedLabel}
              </span>
            ) : (
              <span className="text-gray-500">Select Event Type</span>
            ),
        }}
      >
        {eventTypes.length === 0 ? (
          <MenuItem value="">No event types yet</MenuItem>
        ) : null}
        {value && !selected && currentLabel ? (
          <MenuItem value={value}>
            <TypeDot color={currentColor || cpaGold} />
            {currentLabel}
          </MenuItem>
        ) : null}
        {eventTypes.map((eventType) => (
          <MenuItem value={eventType.id} key={eventType.id}>
            <TypeDot color={eventType.color} />
            {eventType.label}
          </MenuItem>
        ))}
      </TextField>
      <div className="mt-2 flex gap-2">
        <WizardActionButton tone="black" size="small" onClick={openAdd}>
          Add Event Type
        </WizardActionButton>
        <WizardActionButton
          tone="gold"
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={openEdit}
        >
          Edit
        </WizardActionButton>
        <WizardActionButton
          tone="red"
          size="small"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => selected && setConfirmDelete(true)}
        >
          Delete
        </WizardActionButton>
      </div>

      <LibraryEditDialog
        open={Boolean(dialog)}
        title={dialog === "edit" ? "Edit Event Type" : "Add Event Type"}
        confirmLabel={dialog === "edit" ? "Edit Event Type" : "Save Event Type"}
        confirmDisabled={creating || updating || !draftLabel.trim()}
        onConfirm={handleSave}
        onClose={() => setDialog(null)}
      >
        {/* The mock reuses the "Add Event Title" placeholder here; the quirk
            policy corrects it to "Add Event Type". */}
        <TextField
          fullWidth
          size="small"
          placeholder="Add Event Type"
          value={draftLabel}
          onChange={({ target }) => setDraftLabel(target.value)}
          sx={wizardTextFieldSx}
        />
        <div className="mt-6">
          <FieldLabel required hint="This color will represent the event type across your site.">
            Select Event Type Color
          </FieldLabel>
          <ColorPicker value={draftColor} onChange={setDraftColor} />
        </div>
      </LibraryEditDialog>

      {/* The mock's confirm popup reads "Delete Event Title"; the quirk policy
          corrects the heading to the event-type noun. */}
      <DeleteConfirmModal
        open={confirmDelete}
        heading="Delete Event Type"
        name={selected?.label || ""}
        loading={archiving}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <SuccessPopup
        open={Boolean(result)}
        title={result || ""}
        onClose={() => setResult(null)}
      />
    </div>
  );
};

export default EventTypeSelect;
